import { Box, SimpleGrid, Spinner, Text } from '@chakra-ui/core';
import React, { memo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { SectionLabel } from '../SectionLabel';
import { BackgroundImage } from '../videoEffectTypes';
import { VideoBackgroundCard } from './VideoBackgroundCard';
import { useVirtualBackgrounds } from './useVirtualBackgrounds';
import { VideoEffectStore } from '../store/videoEffectModel';

const VB_GRID_MIN_HEIGHT = '145px';

const SCROLLBAR_SX = {
  '&::-webkit-scrollbar': { width: '4px' },
  '&::-webkit-scrollbar-track': { bg: 'transparent' },
  '&::-webkit-scrollbar-thumb': { bg: 'gray.200', borderRadius: '2px' },
} as const;

interface VideoVirtualBackgroundProps {
  onBackgroundSelect: (bg: BackgroundImage | null) => void;
}

const InitialLoader = memo(function InitialLoader() {
  return (
    <Box display="flex" alignItems="center" justifyContent="center" minH={VB_GRID_MIN_HEIGHT}>
      <Spinner size="sm" color="gray.400" />
    </Box>
  );
});

const ErrorState = memo(function ErrorState({ message }: { message: string }) {
  return (
    <Box display="flex" alignItems="center" justifyContent="center" minH={VB_GRID_MIN_HEIGHT}>
      <Text fontSize="sm" color="red.400" textAlign="center">
        {message}
      </Text>
    </Box>
  );
});

const EmptyState = memo(function EmptyState() {
  return (
    <Box display="flex" alignItems="center" justifyContent="center" minH={VB_GRID_MIN_HEIGHT}>
      <Text fontSize="sm" color="gray.400">
        No backgrounds available.
      </Text>
    </Box>
  );
});

interface BackgroundGridProps {
  backgrounds: BackgroundImage[];
  selectedBackground: string | null;
  onBackgroundSelect: (bg: BackgroundImage | null) => void;
}

const BackgroundGrid = memo(function BackgroundGrid({
  backgrounds,
  selectedBackground,
  onBackgroundSelect,
}: BackgroundGridProps) {
  return (
    <SimpleGrid spacing={2} columns={3}>
      {backgrounds.map((bg) => (
        <BackgroundGridItem key={bg.id} bg={bg} isActive={selectedBackground === bg.id} onBackgroundSelect={onBackgroundSelect} />
      ))}
    </SimpleGrid>
  );
});

interface BackgroundGridItemProps {
  bg: BackgroundImage;
  isActive: boolean;
  onBackgroundSelect: (bg: BackgroundImage | null) => void;
}

const BackgroundGridItem = memo(function BackgroundGridItem({ bg, isActive, onBackgroundSelect }: BackgroundGridItemProps) {
  const handleClick = useCallback(() => {
    onBackgroundSelect(isActive ? null : bg);
  }, [bg, isActive, onBackgroundSelect]);

  return <VideoBackgroundCard image={bg} isActive={isActive} onClick={handleClick} isApplying={false} isLoading={false} />;
});

export const VideoVirtualBackground = memo(function VideoVirtualBackground({ onBackgroundSelect }: VideoVirtualBackgroundProps) {
  const { t } = useTranslation('videoInterviewEffect');
  const selectedBackground = VideoEffectStore.useStoreState((state) => state.activeBackgroundId);
  const { backgrounds, isInitialLoading, refreshError } = useVirtualBackgrounds();

  const renderContent = () => {
    if (isInitialLoading) return <InitialLoader />;

    if (refreshError && backgrounds.length === 0) {
      return <ErrorState message={t('virtualBackgrounds.backgroundLoadError')} />;
    }

    if (backgrounds.length === 0) return <EmptyState />;

    return (
      <BackgroundGrid backgrounds={backgrounds} selectedBackground={selectedBackground} onBackgroundSelect={onBackgroundSelect} />
    );
  };

  return (
    <Box display="flex" flexDirection="column" flex="1" minH="0">
      <Box flexShrink={0}>
        <SectionLabel>{t('virtualBackgrounds.title')}</SectionLabel>
      </Box>

      <Box flex="1" minH={VB_GRID_MIN_HEIGHT} overflowY="auto" overflowX="hidden" sx={SCROLLBAR_SX} padding="3px">
        {renderContent()}
      </Box>
    </Box>
  );
});
